/**
 * Shared geo fixtures for fallback usage.
 *
 * Semantics:
 * - fixtures is an array: return it unchanged (including empty array).
 * - fixtures is null/undefined: return locale-based regional fallback fixtures.
 */
import { isPlainObject } from '~/jobiqo/lib/frontendConfigOverrides';

type Region = 'DE' | 'UK' | 'US';

type Location = {
  lat: number;
  lng: number;
};

export type GeoFixture = {
  label: string;
  location?: Location;
  isFixture?: boolean;
  locationType?: string;
  placeId?: string;
};

type SkipSuggestSuggestion = {
  description?: string;
  label?: string;
};

const REGIONAL_FIXTURE_MAP: Record<Region, GeoFixture[]> = {
  DE: [
    {
      label: 'Berlin, Deutschland',
      location: { lat: 52.52, lng: 13.405 },
      isFixture: true,
    },
    {
      label: 'Stuttgart, Deutschland',
      location: { lat: 48.7775, lng: 9.18 },
      isFixture: true,
    },
    {
      label: 'München, Deutschland',
      location: { lat: 48.1375, lng: 11.575 },
      isFixture: true,
    },
    {
      label: 'Hamburg, Deutschland',
      location: { lat: 53.55, lng: 10 },
      isFixture: true,
    },
    {
      label: 'Köln, Deutschland',
      location: { lat: 50.9364, lng: 6.9528 },
      isFixture: true,
    },
    {
      label: 'Frankfurt, Deutschland',
      location: { lat: 50.1106, lng: 8.6822 },
      isFixture: true,
    },
    {
      label: 'Düsseldorf, Deutschland',
      location: { lat: 51.2333, lng: 6.7833 },
      isFixture: true,
    },
    {
      label: 'Leipzig, Deutschland',
      location: { lat: 51.34, lng: 12.375 },
      isFixture: true,
    },
    {
      label: 'Dortmund, Deutschland',
      location: { lat: 51.5139, lng: 7.4653 },
      isFixture: true,
    },
    {
      label: 'Essen, Deutschland',
      location: { lat: 51.4508, lng: 7.0131 },
      isFixture: true,
    },
    {
      label: 'Bremen, Deutschland',
      location: { lat: 53.0758, lng: 8.8072 },
      isFixture: true,
    },
    {
      label: 'Dresden, Deutschland',
      location: { lat: 51.05, lng: 13.74 },
      isFixture: true,
    },
    {
      label: 'Hannover, Deutschland',
      location: { lat: 52.3667, lng: 9.7167 },
      isFixture: true,
    },
    {
      label: 'Nürnberg, Deutschland',
      location: { lat: 49.4539, lng: 11.0775 },
      isFixture: true,
    },
  ],
  UK: [
    {
      label: 'London, United Kingdom',
      location: { lat: 51.5072, lng: -0.1275 },
      isFixture: true,
    },
    {
      label: 'Birmingham, United Kingdom',
      location: { lat: 52.48, lng: -1.9025 },
      isFixture: true,
    },
    {
      label: 'Portsmouth, United Kingdom',
      location: { lat: 50.8058, lng: -1.0872 },
      isFixture: true,
    },
    {
      label: 'Southampton, United Kingdom',
      location: { lat: 50.9025, lng: -1.4042 },
      isFixture: true,
    },
    {
      label: 'Bristol, United Kingdom',
      location: { lat: 51.4536, lng: -2.5975 },
      isFixture: true,
    },
    {
      label: 'Glasgow, United Kingdom',
      location: { lat: 55.8611, lng: -4.25 },
      isFixture: true,
    },
    {
      label: 'Manchester, United Kingdom',
      location: { lat: 53.479, lng: -2.2452 },
      isFixture: true,
    },
    {
      label: 'Leeds, United Kingdom',
      location: { lat: 53.7975, lng: -1.5436 },
      isFixture: true,
    },
    {
      label: 'Sheffield, United Kingdom',
      location: { lat: 53.3808, lng: -1.4703 },
      isFixture: true,
    },
    {
      label: 'Liverpool, United Kingdom',
      location: { lat: 53.4072, lng: -2.9917 },
      isFixture: true,
    },
    {
      label: 'Edinburgh, United Kingdom',
      location: { lat: 55.95, lng: -3.1833 },
      isFixture: true,
    },
    {
      label: 'Worthing, United Kingdom',
      location: { lat: 50.8147, lng: -0.3714 },
      isFixture: true,
    },
    {
      label: 'Leicester, United Kingdom',
      location: { lat: 52.6361, lng: -1.1331 },
      isFixture: true,
    },
    {
      label: 'Barnet, United Kingdom',
      location: { lat: 51.6444, lng: -0.1997 },
      isFixture: true,
    },
    {
      label: 'Newham, United Kingdom',
      location: { lat: 51.5275, lng: 0.0361 },
      isFixture: true,
    },
    {
      label: 'Coventry, United Kingdom',
      location: { lat: 52.4081, lng: -1.5106 },
      isFixture: true,
    },
    {
      label: 'Caerdydd, United Kingdom',
      location: { lat: 51.4817, lng: -3.1792 },
      isFixture: true,
    },
  ],
  US: [
    {
      label: 'New York, United States',
      location: { lat: 40.6943, lng: -73.9249 },
      isFixture: true,
    },
    {
      label: 'Los Angeles, United States',
      location: { lat: 34.1141, lng: -118.4068 },
      isFixture: true,
    },
    {
      label: 'Chicago, United States',
      location: { lat: 41.8375, lng: -87.6866 },
      isFixture: true,
    },
    {
      label: 'Miami, United States',
      location: { lat: 25.784, lng: -80.2101 },
      isFixture: true,
    },
    {
      label: 'Houston, United States',
      location: { lat: 29.786, lng: -95.3885 },
      isFixture: true,
    },
    {
      label: 'Dallas, United States',
      location: { lat: 32.7935, lng: -96.7667 },
      isFixture: true,
    },
    {
      label: 'Philadelphia, United States',
      location: { lat: 40.0077, lng: -75.1339 },
      isFixture: true,
    },
    {
      label: 'Atlanta, United States',
      location: { lat: 33.7628, lng: -84.422 },
      isFixture: true,
    },
    {
      label: 'Washington, United States',
      location: { lat: 38.9047, lng: -77.0163 },
      isFixture: true,
    },
    {
      label: 'Boston, United States',
      location: { lat: 42.3188, lng: -71.0852 },
      isFixture: true,
    },
    {
      label: 'Phoenix, United States',
      location: { lat: 33.5722, lng: -112.0892 },
      isFixture: true,
    },
    {
      label: 'Detroit, United States',
      location: { lat: 42.3834, lng: -83.1024 },
      isFixture: true,
    },
    {
      label: 'Seattle, United States',
      location: { lat: 47.6211, lng: -122.3244 },
      isFixture: true,
    },
    {
      label: 'San Francisco, United States',
      location: { lat: 37.7558, lng: -122.4449 },
      isFixture: true,
    },
    {
      label: 'San Diego, United States',
      location: { lat: 32.8313, lng: -117.1222 },
      isFixture: true,
    },
    {
      label: 'Tampa, United States',
      location: { lat: 27.9945, lng: -82.4447 },
      isFixture: true,
    },
    {
      label: 'Minneapolis, United States',
      location: { lat: 44.9635, lng: -93.2678 },
      isFixture: true,
    },
    {
      label: 'Brooklyn, United States',
      location: { lat: 40.6501, lng: -73.9496 },
      isFixture: true,
    },
    {
      label: 'Denver, United States',
      location: { lat: 39.762, lng: -104.8758 },
      isFixture: true,
    },
  ],
};

const cloneFixtures = (fixtures: GeoFixture[]): GeoFixture[] =>
  fixtures.map((fixture) => ({
    ...fixture,
    ...(fixture.location
      ? {
          location: {
            ...fixture.location,
          },
        }
      : null),
  }));

const getRegionFromLocale = ({
  language,
}: {
  language?: string;
}): Region | null => {
  if (language === 'en') {
    return 'US';
  }

  if (language === 'de') {
    return 'DE';
  }

  if (language === 'en-GB') {
    return 'UK';
  }

  return null;
};

const normalizeGeoLabel = (label?: string): string =>
  (label ?? '').trim().toLowerCase();

export const createGeoFixtureSkipSuggest = (fixtures: GeoFixture[]) => {
  const fixtureLabels = new Set(
    fixtures.map((fixture) => normalizeGeoLabel(fixture.label)),
  );

  return (suggestion: SkipSuggestSuggestion): boolean => {
    if (!('description' in suggestion) || !suggestion.description) {
      return false;
    }

    const suggestionLabel = normalizeGeoLabel(suggestion.description);

    return suggestionLabel !== '' && fixtureLabels.has(suggestionLabel);
  };
};

export const resolveGeoFixtures = ({
  fixtures,
  language,
  locales,
}: {
  fixtures?: GeoFixture[] | string[] | null | Record<string, GeoFixture[]>;
  language?: string;
  locales?: string[];
}): GeoFixture[] | string[] => {
  // Don't fill fixtures when running Playwright tests.
  // We already have default fixtures for tests.
  if (process.env.PLAYWRIGHT_TESTING === 'true') {
    return [];
  }

  if (Array.isArray(fixtures)) {
    return fixtures;
  }

  // To support multilingual sites we can define geo fixtures as an object
  // of fixtures mapped to specific languages and then use the current language
  // to select the correct fixtures.
  if (isPlainObject(fixtures)) {
    if (
      locales &&
      locales.length > 1 &&
      language &&
      fixtures.hasOwnProperty(language)
    ) {
      return fixtures[language];
    } else {
      return [];
    }
  }

  if (fixtures == null) {
    const region = getRegionFromLocale({ language });
    if (region) {
      return cloneFixtures(REGIONAL_FIXTURE_MAP[region]);
    }
    return [];
  }

  return fixtures;
};

export { REGIONAL_FIXTURE_MAP };
